import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "../../services/socket-io";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useAuth = () => {
	const history = useHistory();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});

	api.interceptors.request.use(
		config => {
			let token = localStorage.getItem("token");
			if (token === "undefined" || token === "null") {
				localStorage.removeItem("token");
				token = null;
			}
			if (token) {
				let parsedToken = token;
				try { parsedToken = JSON.parse(token); } catch(e) { parsedToken = token; }
				config.headers["Authorization"] = `Bearer ${parsedToken}`;
				setIsAuth(true);
			}
			return config;
		},
		error => {
			Promise.reject(error);
		}
	);

	api.interceptors.response.use(
		response => {
			return response;
		},
		async error => {
			const originalRequest = error.config;
			if (error?.response?.status === 403 && !originalRequest._retry) {
				originalRequest._retry = true;

				try {
					const { data } = await api.post("/auth/refresh_token");
					if (data) {
						localStorage.setItem("token", JSON.stringify(data.token));
						if (data.user) {
							localStorage.setItem("user", JSON.stringify(data.user));
							setUser(data.user);
						}
						api.defaults.headers.Authorization = `Bearer ${data.token}`;
						return api(originalRequest);
					}
				} catch (refreshErr) {
					localStorage.removeItem("token");
					localStorage.removeItem("user");
					api.defaults.headers.Authorization = undefined;
					setIsAuth(false);
					return Promise.reject(refreshErr);
				}
			}
			if (error?.response?.status === 401) {
				localStorage.removeItem("token");
				localStorage.removeItem("user");
				api.defaults.headers.Authorization = undefined;
				setIsAuth(false);
			}
			return Promise.reject(error);
		}
	);

	useEffect(() => {
		let token = localStorage.getItem("token");
		if (token === "undefined" || token === "null") {
			localStorage.removeItem("token");
			token = null;
		}
		let cachedUser = null;
		try {
			cachedUser = JSON.parse(localStorage.getItem("user"));
		} catch(e) {}

		(async () => {
			if (token) {
				let parsedToken = token;
				try { parsedToken = JSON.parse(token); } catch(e) { parsedToken = token; }
				api.defaults.headers.Authorization = `Bearer ${parsedToken}`;
				setIsAuth(true);
				if (cachedUser) {
					setUser(cachedUser);
				}

				try {
					const { data } = await api.post("/auth/refresh_token");
					if (data) {
						localStorage.setItem("token", JSON.stringify(data.token));
						api.defaults.headers.Authorization = `Bearer ${data.token}`;
						setIsAuth(true);
						const finalUser = data.user || cachedUser || {};
						setUser(finalUser);
						localStorage.setItem("user", JSON.stringify(finalUser));
					}
				} catch (err) {
					// Only log out if refresh token failed AND we got a hard 401/403
					if (err?.response?.status === 401 || err?.response?.status === 403) {
						localStorage.removeItem("token");
						localStorage.removeItem("user");
						api.defaults.headers.Authorization = undefined;
						setIsAuth(false);
					}
				}
			}
			setLoading(false);
		})();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		socket.on("user", data => {
			if (data.action === "update" && data.user.id === user.id) {
				setUser(data.user || {});
				localStorage.setItem("user", JSON.stringify(data.user));
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [user]);

	const handleLogin = async userData => {
		setLoading(true);

		try {
			const { data } = await api.post("/auth/login", userData);
			localStorage.setItem("token", JSON.stringify(data.token));
			localStorage.setItem("user", JSON.stringify(data.user));
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			setIsAuth(true);
			toast.success(i18n.t("auth.toasts.success"));
			history.push("/tickets");
			setLoading(false);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	const handleLogout = async () => {
		setLoading(true);

		try {
			await api.delete("/auth/logout");
			setIsAuth(false);
			setUser({});
			localStorage.removeItem("token");
			localStorage.removeItem("user");
			api.defaults.headers.Authorization = undefined;
			setLoading(false);
			history.push("/login");
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	return { isAuth, user, loading, handleLogin, handleLogout };
};

export default useAuth;
